import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import {ExternalLink} from 'lucide-react';

interface UsefulLink {
  label: string;
  url: string;
  description?: string;
}

interface LinkCategory {
  title: string;
  accent: "red" | "blue" | "gray";
  links: UsefulLink[];
}

const CATEGORIES: LinkCategory[] = [
  {
    title: "Organismes officiels",
    accent: "red",
    links: [
      {
        label: "Ministère des Sports et des Jeux Olympiques et Paralympiques",
        url: "https://www.sports.gouv.fr",
        description: "Réglementation officielle des diplômes de surveillance et d'enseignement aquatique (BNSSA, SSA, BPJEPS, DEJEPS).",
      },
      {
        label: "Service-public.fr",
        url: "https://www.service-public.fr",
        description: "Démarches administratives, réglementation et informations officielles sur les métiers aquatiques.",
      },
      {
        label: "France Travail",
        url: "https://www.francetravail.fr",
        description: "Offres d'emploi complémentaires et accompagnement à la recherche d'emploi.",
      },
      {
        label: "Légifrance",
        url: "https://www.legifrance.gouv.fr",
        description: "Textes de loi et arrêtés encadrant les diplômes et la surveillance des baignades.",
      },
    ],
  },
  {
    title: "Fédérations et syndicats professionnels",
    accent: "blue",
    links: [
      {
        label: "Fédération Française de Sauvetage et de Secourisme (FFSS)",
        url: "https://www.ffss.fr",
        description: "Formation au sauvetage aquatique et préparation au BNSSA/SSA.",
      },
      {
        label: "Fédération Française de Natation (FFN)",
        url: "https://www.ffnatation.fr",
        description: "Fédération de référence pour la natation et les activités aquatiques en France.",
      },
      {
        label: "SNPMNS — Syndicat National des Professionnels Maîtres-Nageurs Sauveteurs",
        url: "https://www.snpmns.org",
        description: "Actualités, défense de la profession et suivi des réformes de diplômes (BPJEPS AAN/NAA).",
      },
      {
        label: "Société Nationale de Sauvetage en Mer (SNSM)",
        url: "https://www.snsm.org",
        description: "Sauvetage en mer et engagement bénévole ou professionnel sur le littoral.",
      },
    ],
  },
  {
    title: "Formation et réglementation",
    accent: "gray",
    links: [
      {
        label: "DRAJES — Délégations régionales académiques à la jeunesse, à l'engagement et aux sports",
        url: "https://www.sports.gouv.fr/les-services-du-ministere-des-sports-1005",
        description: "Organisation des examens et sessions officielles du BNSSA dans chaque région.",
      },
      {
        label: "Portail des ministères de l'Éducation nationale et des Sports",
        url: "https://www.education.gouv.fr",
        description: "Informations sur les diplômes de l'animation et du sport (BPJEPS, DEJEPS, DESJEPS).",
      },
    ],
  },
  {
    title: "Annuaires web",
    accent: "gray",
    links: [
      {
        label: "Actimonde - Annuaire web gratuit",
        url: "https://www.actimonde.com/",
        description: "Annuaire web gratuit de sites de qualité.",
      },
    ],
  },
];

const ACCENT_STYLES: Record<LinkCategory["accent"], { dot: string; hover: string }> = {
  red: { dot: "bg-red-500", hover: "hover:border-red-200 hover:bg-red-50/40" },
  blue: { dot: "bg-blue-500", hover: "hover:border-blue-200 hover:bg-blue-50/40" },
  gray: { dot: "bg-gray-400", hover: "hover:border-gray-300 hover:bg-gray-50" },
};

export default function UsefulLinks() {
  const canonical = "https://www.bnssa-mns.fr/liens-utiles";
  const title = "Liens utiles — Emplois BNSSA & MNS";
  const description =
    "Retrouvez une sélection de liens utiles pour les métiers de la surveillance et de l'enseignement aquatique : organismes officiels, fédérations, syndicats et ressources de formation BNSSA et Maître-Nageur Sauveteur.";

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.bnssa-mns.fr/" },
      { "@type": "ListItem", position: 2, name: "Liens utiles", item: canonical },
    ],
  };

  return (
    <Layout>
      <Seo title={title} description={description} canonical={canonical} jsonLd={breadcrumbJsonLd} />

      <section className="bg-gray-50 border-b border-gray-200 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Fil d'Ariane" className="mb-4">
            <ol className="flex items-center gap-2 text-sm text-gray-400">
              <li>
                <a href="/" className="hover:text-gray-600 transition-colors">
                  Accueil
                </a>
              </li>
              <li aria-hidden="true" className="text-gray-300">›</li>
              <li className="text-gray-700 font-medium">Liens utiles</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Liens utiles</h1>
          <p className="text-gray-600 leading-relaxed max-w-2xl">
            Une sélection de ressources officielles et professionnelles pour les candidats et
            recruteurs des métiers du BNSSA et du Maître-Nageur Sauveteur : réglementation,
            fédérations, syndicats et organismes de formation.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {CATEGORIES.map((category) => {
            const accent = ACCENT_STYLES[category.accent];
            return (
              <div key={category.title}>
                <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-4">
                  <span className={`w-2 h-2 rounded-full inline-block ${accent.dot}`} />
                  {category.title}
                </h2>
                <ul className="space-y-3">
                  {category.links.map((link) => (
                    <li key={link.url}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`block rounded-xl border border-gray-200 p-4 transition-colors ${accent.hover}`}
                      >
                        <span className="flex items-start justify-between gap-3">
                          <span className="font-semibold text-gray-900">{link.label}</span>
                          <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        </span>
                        {link.description ? (
                          <span className="block text-sm text-gray-500 mt-1.5 leading-relaxed">
                            {link.description}
                          </span>
                        ) : null}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}
